import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { MetadataList } from '../../shared/detail/MetadataList';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { mockAssignmentAgents } from './fixtures';
import { memberRepositoryToken } from './repository';
import type {
  AssignmentChangeSet,
  AssignmentPreview,
  AssignmentPreviewInput,
  MemberDetail,
} from './types';

interface AssignmentForm {
  readonly agentId: string;
  readonly uplineMemberId: string;
  readonly effectiveAt: string;
  readonly reason: string;
}

export function AssignmentChangeDialog({
  dataSource,
  member,
  onClose,
  onSaved,
  open,
}: {
  readonly dataSource: DataSource;
  readonly member: MemberDetail;
  readonly onClose: () => void;
  readonly onSaved: (changeSet: AssignmentChangeSet) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(memberRepositoryToken);
  const { t } = useTranslation();
  const previewGuard = useRef(false);
  const submitGuard = useRef(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState<AssignmentPreview | null>(null);
  const [previewFingerprint, setPreviewFingerprint] = useState<string | null>(null);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<AssignmentForm>({
    defaultValues: {
      agentId: member.currentAssignment?.agent.id ?? '',
      uplineMemberId: member.currentAssignment?.upline?.memberId ?? '',
      effectiveAt: '',
      reason: '',
    },
  });
  const watched = watch();
  const normalized = useMemo(() => normalizeInput(member.id, watched), [member.id, watched]);
  const fingerprint = useMemo(() => JSON.stringify(normalized), [normalized]);
  const previewCurrent = preview !== null && previewFingerprint === fingerprint;

  useEffect(() => {
    if (!open) return;
    reset({
      agentId: member.currentAssignment?.agent.id ?? '',
      uplineMemberId: member.currentAssignment?.upline?.memberId ?? '',
      effectiveAt: '',
      reason: '',
    });
    setPreview(null);
    setPreviewFingerprint(null);
    setConfirmed(false);
    setError(null);
    previewGuard.current = false;
    submitGuard.current = false;
    setPreviewBusy(false);
    setSubmitBusy(false);
  }, [member.currentAssignment, open, reset]);

  useEffect(() => {
    if (!previewCurrent) setConfirmed(false);
  }, [previewCurrent]);

  const close = () => {
    if (!previewGuard.current && !submitGuard.current) onClose();
  };
  const previewChange = handleSubmit(async (values) => {
    if (previewGuard.current || submitGuard.current) return;
    previewGuard.current = true;
    setPreviewBusy(true);
    setError(null);
    const input = normalizeInput(member.id, values);
    try {
      const next = await repository.previewAssignment(input);
      setPreview(next);
      setPreviewFingerprint(JSON.stringify(input));
      setConfirmed(false);
    } catch (previewError) {
      setPreview(null);
      setPreviewFingerprint(null);
      setError(toDialogError(previewError, t('members.errors.preview')));
    } finally {
      previewGuard.current = false;
      setPreviewBusy(false);
    }
  });
  const submit = async () => {
    if (
      submitGuard.current ||
      previewGuard.current ||
      !previewCurrent ||
      preview === null ||
      !confirmed ||
      preview.blockers.length > 0
    )
      return;
    const latestInput = normalizeInput(member.id, getValues());
    if (JSON.stringify(latestInput) !== previewFingerprint) return;
    submitGuard.current = true;
    setSubmitBusy(true);
    setError(null);
    try {
      const result = await repository.createAssignmentChangeSet({
        ...latestInput,
        previewHash: preview.previewHash,
      });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('members.errors.assignment')));
      setPreview(null);
      setPreviewFingerprint(null);
      setConfirmed(false);
    } finally {
      submitGuard.current = false;
      setSubmitBusy(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" onClose={close} open={open}>
      <DialogTitle>{t('members.assignment.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="assignment-preview-form"
          noValidate
          onSubmit={previewChange}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            {t('members.assignment.impact', { member: member.username ?? member.id })}
          </Alert>
          {dataSource === 'mock' ? (
            <Controller
              control={control}
              name="agentId"
              rules={{ required: t('members.validation.agentRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.agentId !== undefined}
                  helperText={errors.agentId?.message}
                  label={t('members.fields.agent')}
                  required
                  select
                >
                  {mockAssignmentAgents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.code}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ) : (
            <TextField
              error={errors.agentId !== undefined}
              helperText={errors.agentId?.message}
              label={t('members.fields.agent')}
              required
              {...register('agentId', {
                required: t('members.validation.agentRequired'),
                validate: (value) =>
                  value.trim().length > 0 || t('members.validation.agentRequired'),
              })}
            />
          )}
          <TextField
            error={errors.uplineMemberId !== undefined}
            helperText={errors.uplineMemberId?.message}
            label={t('members.fields.upline')}
            required
            {...register('uplineMemberId', {
              required: t('members.validation.uplineRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.uplineRequired'),
            })}
          />
          <TextField
            error={errors.effectiveAt !== undefined}
            helperText={errors.effectiveAt?.message ?? t('members.assignment.futureOnly')}
            label={t('members.fields.effectiveAt')}
            required
            slotProps={{ inputLabel: { shrink: true } }}
            type="datetime-local"
            {...register('effectiveAt', {
              required: t('members.validation.effectiveRequired'),
              validate: (value) => {
                const timestamp = Date.parse(value);
                return (
                  (Number.isFinite(timestamp) && timestamp > Date.now()) ||
                  t('members.validation.futureRequired')
                );
              },
            })}
          />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('members.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('members.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.reasonRequired'),
            })}
          />
          <Button loading={previewBusy} type="submit" variant="outlined">
            {t('members.actions.preview')}
          </Button>
        </Stack>
        {preview === null ? null : previewCurrent ? (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Alert severity={preview.blockers.length > 0 ? 'error' : 'success'}>
              <Typography sx={{ fontWeight: 700 }}>
                {t('members.assignment.previewHash')}
              </Typography>
              <Typography component="code" sx={{ overflowWrap: 'anywhere' }}>
                {preview.previewHash}
              </Typography>
              <Typography variant="body2">{t('members.assignment.previewBound')}</Typography>
            </Alert>
            <Stack
              aria-label={t('members.assignment.previewChangesLabel')}
              component="section"
              role="region"
              spacing={1}
            >
              <Typography component="h3" variant="h6">
                {t('members.assignment.previewChanges')}
              </Typography>
              {preview.changes.map((change, index) => (
                <Paper
                  key={`${change.memberId}-${index}`}
                  component="article"
                  sx={{ p: 1.5 }}
                  variant="outlined"
                >
                  <MetadataList
                    items={[
                      { label: t('members.assignment.memberId'), value: change.memberId },
                      {
                        label: t('members.assignment.beforeAgent'),
                        value: change.before?.agentId ?? t('common.notAvailable'),
                      },
                      {
                        label: t('members.assignment.beforeUpline'),
                        value: change.before?.uplineMemberId ?? t('common.notAvailable'),
                      },
                      {
                        label: t('members.assignment.afterAgent'),
                        value: change.after.agentId,
                      },
                      {
                        label: t('members.assignment.afterUpline'),
                        value: change.after.uplineMemberId,
                      },
                    ]}
                  />
                </Paper>
              ))}
            </Stack>
            {preview.warnings.map((notice) => (
              <Alert key={`${notice.code}-${notice.memberId ?? ''}`} severity="warning">
                {notice.message}
              </Alert>
            ))}
            {preview.blockers.map((notice) => (
              <Alert key={`${notice.code}-${notice.memberId ?? ''}`} severity="error">
                {notice.message}
              </Alert>
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(_event, checked) => setConfirmed(checked)}
                />
              }
              label={t('members.assignment.confirm')}
            />
          </Stack>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('members.assignment.expired')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={previewBusy || submitBusy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          disabled={
            !previewCurrent || !confirmed || (preview?.blockers.length ?? 0) > 0 || previewBusy
          }
          loading={submitBusy}
          onClick={submit}
          variant="contained"
        >
          {t('members.actions.submitAssignment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function normalizeInput(memberId: string, values: AssignmentForm): AssignmentPreviewInput {
  const parsed = Date.parse(values.effectiveAt);
  return {
    mode: 'SCHEDULED',
    effectiveAt: Number.isFinite(parsed) ? new Date(parsed).toISOString() : values.effectiveAt,
    reason: values.reason.trim(),
    changes: [
      {
        memberId,
        agentId: values.agentId.trim(),
        uplineMemberId: values.uplineMemberId.trim(),
      },
    ],
  };
}
